import { combineReducers, type Middleware } from "redux"
import { configureStore, type PayloadAction } from "@reduxjs/toolkit"
import storage from "redux-persist/lib/storage"
import { persistReducer, purgeStoredState } from "redux-persist"
import { userSlice } from "./redux/user"
import { uiSlice } from "./redux/ui"
import { settingsSlice } from "./redux/settings"
import { draftSlice } from "./redux/draft"
import { api } from "./api/api"
import { visualizationSlice } from "./redux/visualization"
import { registerSlice } from "./redux/register"
import { markdownSlice } from "./redux/markdown"
import { statusSlice } from "@redux/statusMessage"
import { createStateSyncMiddleware, initMessageListener } from "redux-state-sync"
import { authSlice, authListener } from "@myeway/auth"
import { tabsSlice } from "@redux/tabs"
import { playerCardAnalysisSlice } from "@redux/playerCardAnalysis"
import { unfinishedDiariesSlice } from "@redux/unfinishedDiaries"
import { rateLimitSlice } from "@redux/rateLimit"

const persistConfig = {
	key: "root",
	storage,
	whitelist: ["auth", "user", "draft", "unfinishedDiaries"],
	blacklist: ["settings"],
}

const reduxStateSyncConfig = {
	whitelist: ["auth/logoutUser", "user/login/fulfilled"],
}

const settingsPersistConfig = {
	key: "settings",
	storage,
	blacklist: ["selectedDate", "calendarInitialized", "selectedCalendar"],
}

const reducers = combineReducers({
	user: userSlice.reducer,
	register: registerSlice.reducer,
	ui: uiSlice.reducer,
	settings: persistReducer(settingsPersistConfig, settingsSlice.reducer),
	draft: draftSlice.reducer,
	visualization: visualizationSlice.reducer,
	markdown: markdownSlice.reducer,
	statusMessage: statusSlice.reducer,
	auth: authSlice.reducer,
	tabs: tabsSlice.reducer,
	playerCardAnalysis: playerCardAnalysisSlice.reducer,
	unfinishedDiaries: unfinishedDiariesSlice.reducer,
	rateLimit: rateLimitSlice.reducer,
	[api.reducerPath]: api.reducer,
})

type ReducersType = typeof reducers
const rootReducer = (state: ReturnType<ReducersType> | undefined, action: PayloadAction<{ type: string }>) => {
	if (action.type === "FATAL_ERROR") {
		purgeStoredState(persistConfig)
		state = undefined
		window.location.href = "/error"
	} else if (action.type.startsWith("auth/logout")) {
		// Setting state to undefined here applies initial state to all reducers
		state = undefined
	}
	return reducers(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: false,
		})
			// Important: must be before other middleware
			.prepend(authListener.middleware)
			.concat(createStateSyncMiddleware(reduxStateSyncConfig) as Middleware)
			.concat(api.middleware),
})

initMessageListener(store)

// Get the type of our store variable
export type AppStore = typeof store
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore["getState"]>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = AppStore["dispatch"]

export default store
